import { useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast, Toaster } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

/** Land the user at their most useful page based on role. */
export function landingFor(user) {
  switch (user?.role) {
    case "super_admin": return "/admin/users";
    case "hod":         return "/hod";
    case "admin1":
    case "admin1_alt":
    case "admin2":      return "/admin";
    case "process_owner": return "/process-owner";
    default:            return "/dashboard";
  }
}

export default function Login() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();

  useEffect(() => {
    if (user) {
      const next = params.get("next") || sessionStorage.getItem("vgws_next");
      sessionStorage.removeItem("vgws_next");
      const dest = next && next.startsWith("/") ? next : landingFor(user);
      toast.success(`Signed in as ${user.name}`);
      nav(dest, { replace: true });
    }
  }, [user, nav, params]);

  useEffect(() => {
    const err = params.get("err");
    if (err === "domain") {
      toast.error(
        "Only @mitgurukul.com accounts are allowed. Please use your school email.",
        { duration: 6000, id: "domain-gate" },
      );
    } else if (err === "session") {
      toast.error("Sign-in was cancelled or expired. Please try again.");
    }
  }, [params]);

  const onGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const next = params.get("next");
    if (next) sessionStorage.setItem("vgws_next", next);
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-paper flex items-center justify-center px-4 py-10" data-testid="login-page">
      <Toaster position="bottom-right" richColors />
      <div className="w-full max-w-4xl grid lg:grid-cols-[420px,1fr] gap-6 items-stretch">
        <div className="sheet p-8 flex flex-col">
          <div className="flex items-center gap-3 mb-6">
            <img
              src="/vgws-logo.svg"
              onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = "/vgws-logo.png"; }}
              alt="VGWS emblem"
              className="w-16 h-16 rounded-full ring-1 ring-[#e2d7c3] shadow-sm"
              data-testid="login-logo"
            />
            <div>
              <div className="font-serif font-bold text-[#7a1f2b] text-base leading-tight">VISHWASHANTI GURUKUL</div>
              <div className="text-[10px] uppercase tracking-widest text-[#6e7770]">World School</div>
              <div className="text-xs text-[#2d6b6b] mt-0.5">Requisition Portal</div>
            </div>
          </div>

          <h1 className="font-serif text-3xl font-bold text-[#1f2320] leading-tight">Sign in</h1>
          <p className="text-sm text-[#4a524c] mt-2">
            Use your Google account to enter the portal. Staff sign in with their
            <span className="text-[#7a1f2b] font-medium"> @mitgurukul.com </span>
            account; parents &amp; students can sign in with any Google account.
          </p>

          <button
            type="button"
            onClick={onGoogle}
            className="mt-8 inline-flex items-center justify-center gap-3 w-full px-4 py-3 rounded-md bg-white border border-[#e2d7c3] hover:border-[#7a1f2b] hover:bg-[#faf5eb] transition-all shadow-sm text-[#1f2320] font-medium"
            data-testid="google-signin-button"
          >
            <GoogleGlyph />
            <span>Continue with Google</span>
          </button>

          <div className="mt-8 rounded-md bg-[#e8f2f2] border border-[#2d6b6b]/30 p-3 text-xs text-[#2d6b6b]">
            <strong>Your role is automatic.</strong> If your email is already
            registered as an HOD / Admin / Process Owner you&apos;ll land on the
            correct dashboard. New users are onboarded as applicants.
          </div>
        </div>

        <div className="sheet-plain p-8 flex flex-col justify-center relative overflow-hidden">
          <img src="/vgws-logo.svg" alt="" aria-hidden="true"
               className="absolute right-4 bottom-4 w-72 h-72 opacity-[0.06] pointer-events-none select-none" />
          <div className="max-w-md">
            <div className="text-[11px] uppercase tracking-widest text-[#2d6b6b] font-semibold mb-3">
              Phase 2c · Ready for UAT
            </div>
            <h2 className="font-serif text-3xl sm:text-4xl font-bold text-[#7a1f2b] leading-tight">
              VGWS Requisition Portal Paperless Flow
            </h2>
            <p className="text-[#4a524c] mt-4 leading-relaxed">
              Requisitions are accepted only on working days and working
              hours. Every update / modification is recorded. Request access
              to view in Drive to download and print the forms.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

function GoogleGlyph() {
  return (
    <svg width="20" height="20" viewBox="0 0 48 48" aria-hidden="true">
      <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.9 32.3 29.4 35 24 35c-6.1 0-11-4.9-11-11s4.9-11 11-11c2.8 0 5.4 1 7.4 2.7l5.7-5.7C33.6 6.7 29 5 24 5 13.5 5 5 13.5 5 24s8.5 19 19 19 19-8.5 19-19c0-1.2-.1-2.4-.4-3.5z"/>
      <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.9 19 13 24 13c2.8 0 5.4 1 7.4 2.7l5.7-5.7C33.6 6.7 29 5 24 5 16.5 5 10 9.4 6.3 14.7z"/>
      <path fill="#4CAF50" d="M24 43c5 0 9.5-1.7 12.9-4.6l-6-5c-2 1.4-4.5 2.3-6.9 2.3-5.4 0-9.9-2.7-11.3-6.9l-6.6 5.1C10 38.6 16.5 43 24 43z"/>
      <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.7 2-2 3.7-3.6 4.9l6 5C41 34.6 43 29.7 43 24c0-1.2-.1-2.4-.4-3.5z"/>
    </svg>
  );
}
